// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Alert, AppIcon, Badge, Checkbox, IconButton, Spinner } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import type { Policy } from '../types/policy';
import { NudgeProviderButton } from './nudge';
import './PolicyList.css';


// Policy workflow status — derived from policy data to drive the Action
// column (nudge / refresh / delete eligibility). The LoA Status column has
// been removed; this type is kept solely for action-button dispatch.
export type PolicyWorkflowStatus =
  | 'invite_not_sent'
  | 'loa_invite_sent'
  | 'loa_partially_signed'
  | 'pending_provider'
  | 'provider_rejected'
  | 'data_available'
  | 'loa_rejected'
  | 'refresh_needed'
  | 'archived';

export function getPolicyWorkflowStatus(policy: Policy): PolicyWorkflowStatus {
  // Archived states
  if (policy.status === 'cancelled' || policy.status === 'expired') return 'archived';

  // Document-based states
  if (policy.documentStatus === 'DOCUMENT_OUTDATED') return 'refresh_needed';
  if (policy.documentStatus === 'HAS_DOCUMENT') return 'data_available';

  // LOA-based states
  const hasCompletedLoa = (policy.completedLoaCount ?? 0) > 0;
  const hasLoa = (policy.loaCount ?? 0) > 0;
  const signatories = policy.signatories || [];
  const someSigned = signatories.some(s => s.isSigned);

  if (hasCompletedLoa) {
    if (policy.providerRejected) return 'provider_rejected';
    return 'pending_provider';
  }

  // "Partially signed" only applies when at least one signatory has
  // signed. An LOA in GENERATING / PENDING_SIGNATURE with zero signed
  // signatories is just awaiting signature — fall through to the
  // invite-based labels below.
  if (hasLoa && someSigned) {
    return 'loa_partially_signed';
  }

  // Either (a) LOA exists but no one has signed yet, or (b) no LOA
  // exists but the clients have been invited. Both map to "LoA Invite
  // sent" from the advisor's point of view — the LOA is ready; we're
  // waiting on the signatories. If the clients haven't been invited
  // yet, show "Invite not sent".
  if (hasLoa) {
    return 'loa_invite_sent';
  }
  if (signatories.length > 0) {
    const allInvited = signatories.every(s => {
      const status = s.signatoryStatus?.toUpperCase();
      return status && status !== 'PENDING_SIGNATURE' && status !== 'DRAFT';
    });
    if (allInvited) return 'loa_invite_sent';
  }

  return 'invite_not_sent';
}


type PolicyListProps = Readonly<{
  policies: Policy[];
  loading: boolean;
  error: string | null;
  onEdit: (policy: Policy) => void;
  onDelete: (policyId: string) => void;
  onManageRelationships: (policy: Policy) => void;
  onRowClick?: (policy: Policy) => void;
  readOnly?: boolean;
  /** Set of policy IDs that have active detail overrides */
  policyIdsWithOverrides?: Set<string>;
  /** Callback to remove a client-policy relationship */
  onRemoveRelationship?: (policy: Policy) => void;
  /** Set of policy IDs where remove is allowed */
  removablePolicyIds?: Set<string>;
  /** Hide the trailing Action column (used by the CoA wizard). */
  hideActionColumn?: boolean;
  /** Render a leading selection checkbox column. */
  selectable?: boolean;
  /** IDs of the currently selected policies (required when {@code selectable}). */
  selectedPolicyIds?: string[];
  /** Toggle a single policy selection. */
  onTogglePolicy?: (policyId: string) => void;
  /** Toggle all visible policies on/off. */
  onToggleAll?: () => void;
  /** Whether every visible policy is currently selected (drives the header checkbox). */
  allSelected?: boolean;
  /** IDs of policies that should appear dimmed with disabled checkboxes (not selectable). */
  disabledPolicyIds?: Set<string>;
}>;

export function PolicyList({
  policies,
  loading,
  error,
  onDelete,
  onRowClick,
  readOnly = false,
  policyIdsWithOverrides,
  onRemoveRelationship,
  removablePolicyIds,
  hideActionColumn = false,
  selectable = false,
  selectedPolicyIds,
  onTogglePolicy,
  onToggleAll,
  allSelected = false,
  disabledPolicyIds,
}: PolicyListProps) {
  const selectedSet = selectable ? new Set(selectedPolicyIds ?? []) : null;
  if (loading) {
    return (
      <div className="policy-list-loading">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="policy-list-error">
        <Alert variant="error" header="Error Loading Policies">
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="policy-list-empty mx-md mb-md">
        <h4 className="policy-list-empty__title">No Policies Found</h4>
        <p className="policy-list-empty__description">Add a policy to this profile to get started.</p>
      </div>
    );
  }

  const getInitials = (name: string | undefined): string => {
    if (!name) return '?';
    const words = name.split(' ').filter(Boolean);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getOwnershipTypeLabel = (type: string) => {
    switch (type) {
      case 'SOLO': return 'Solo';
      case 'JOINT': return 'Joint';
      case 'GROUP': return 'Group';
      default: return type || '-';
    }
  };

  const getOwnerCount = (policy: Policy): number => {
    if (policy.ownerCount !== undefined) return policy.ownerCount;
    return policy.signatories?.filter(
      s => s.relationshipType === 'OWNER' || s.relationshipType === 'OWNER_AND_SIGNATORY'
    ).length || 0;
  };

  const getSignatoryCount = (policy: Policy): number => {
    return policy.signatories?.filter(
      s => s.relationshipType === 'SIGNATORY' || s.relationshipType === 'OWNER_AND_SIGNATORY'
    ).length || 0;
  };

  const renderCoaAgencyStatus = (coaStatus: string) => {
    switch (coaStatus.toUpperCase()) {
      case 'DRAFT':
      case 'PENDING_SIGNATURE':
      case 'PARTIALLY_SIGNED':
        return <Badge color="purple" variant="outlined" size="sm">Pending Signature</Badge>;
      case 'SIGNED':
      case 'PENDING_PROVIDER':
        return <Badge color="blue" variant="outlined" size="sm">CoA Signed</Badge>;
      case 'ACCEPTED':
        return <Badge color="green" variant="outlined" size="sm">In agency</Badge>;
      case 'REJECTED':
        return <Badge color="red" variant="outlined" size="sm">CoA Rejected</Badge>;
      case 'DECLINED':
        return <Badge color="red" variant="outlined" size="sm">CoA Declined</Badge>;
      case 'VOIDED':
        return <Badge color="grey" variant="outlined" size="sm">CoA Voided</Badge>;
      default:
        return <Badge color="grey" variant="outlined" size="sm">Not in agency</Badge>;
    }
  };

  const renderAction = (policy: Policy, workflowStatus: PolicyWorkflowStatus) => {
    if (readOnly) {
      return (
        <span className="policy-view-only">
          <AppIcon name="eye" size={14} />
          View Only
        </span>
      );
    }

    let nudgeButton: React.ReactNode = null;
    if (workflowStatus === 'pending_provider' || workflowStatus === 'refresh_needed') {
      nudgeButton = (
        <NudgeProviderButton
          policyId={policy.id}
          relationshipId={policy.profileId}
          hasCompletedLoa={(policy.completedLoaCount ?? 0) > 0}
          variant="text"
          size="mini"
          label={workflowStatus === 'refresh_needed' ? 'Refresh' : 'Nudge'}
        />
      );
    }

    const canRemove = onRemoveRelationship && removablePolicyIds?.has(policy.id);
    const removeButton = canRemove ? (
      <button
        className="policy-remove-btn focus:outline-none focus-visible:outline-none"
        title="Remove relationship"
        onClick={(e) => {
          e.stopPropagation();
          onRemoveRelationship(policy);
        }}
      >
        <AppIcon name="trash" size={16} className="policy-remove-icon" />
      </button>
    ) : null;

    // Delete-policy button: hidden once any LoA work has progressed past
    // "invite sent" — partially signed, fully signed (pending provider),
    // data returned by provider, or provider rejected. Provider-rejected
    // policies stay because the recovery flow is "fix and resend", not
    // "delete and recreate" — deletion would orphan the LoA history.
    const canDeletePolicy = !!onDelete && ![
      'loa_partially_signed',
      'pending_provider',
      'data_available',
      'provider_rejected',
    ].includes(workflowStatus);
    const deleteButton = canDeletePolicy ? (
      <IconButton
        variant="round-filled"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(policy.id);
        }}
        aria-label={`Delete policy ${policy.policyNumber || policy.id}`}
        title="Delete policy"
        icon={<AppIcon name="trash" />}
        className="policy-delete-btn"
      />
    ) : null;

    if (!nudgeButton && !deleteButton && !removeButton) return null;

    return (
      <div className="policy-action-group">
        {nudgeButton}
        {deleteButton}
        {removeButton}
      </div>
    );
  };

  return (
    <table className="policy-list-table">
      <thead>
        <tr>
          {selectable && (
            <th className="col-select">
              <Checkbox
                checked={allSelected}
                onChange={() => onToggleAll?.()}
                aria-label="Select all policies"
              />
            </th>
          )}
          <th>Provider</th>
          <th>Policy Type</th>
          <th>Policy No.</th>
          <th>Type</th>
          <th className="col-count">Owners</th>
          <th className="col-count">Signatories</th>
          <th>Last updated</th>
          <th>LoA in Place</th>
          <th>Agency status</th>
          {!hideActionColumn && <th className="col-action">Action</th>}
        </tr>
      </thead>
      <tbody>
        {policies.map((policy) => {
          const workflowStatus = getPolicyWorkflowStatus(policy);
          const isDisabled = disabledPolicyIds?.has(policy.id) ?? false;

          return (
            <tr
              key={policy.id}
              className={`${onRowClick ? 'policy-list-table__row--clickable' : ''}${isDisabled ? ' policy-list-table__row--disabled' : ''}`}
              onClick={() => !isDisabled && onRowClick?.(policy)}
            >
              {selectable && (
                <td className="col-select" onClick={(e) => e.stopPropagation()}>
                  <div style={isDisabled ? { opacity: 0.3 } : undefined}>
                    <Checkbox
                      checked={selectedSet?.has(policy.id) ?? false}
                      onChange={() => !isDisabled && onTogglePolicy?.(policy.id)}
                      disabled={isDisabled}
                      aria-label={`Select policy ${policy.policyNumber || policy.id}`}
                    />
                  </div>
                </td>
              )}
              <td>
                <div className="policy-provider">
                  <div className="policy-provider__avatar">
                    {policy.providerLogoUrl ? (
                      <img src={policy.providerLogoUrl} alt={policy.providerName || 'Provider'} />
                    ) : (
                      getInitials(policy.providerName || policy.carrier)
                    )}
                  </div>
                  <span className="policy-provider__name">
                    {policy.providerName || policy.carrier || 'Unknown'}
                  </span>
                  {policyIdsWithOverrides?.has(policy.id) && (
                    <span className="policy-override-indicator" title="Has active detail overrides">
                      <AppIcon name="info circle" size={16} color="#733F01" />
                    </span>
                  )}
                </div>
              </td>
              <td>
                <span className="policy-type">{policy.policyTypeName || policy.category || '-'}</span>
              </td>
              <td>
                <span className="policy-number">{policy.policyNumber || '-'}</span>
              </td>
              <td>
                <span className="policy-ownership">{getOwnershipTypeLabel(policy.ownershipType)}</span>
              </td>
              <td className="col-count">
                <span className="policy-count-badge policy-count-badge--owners">{getOwnerCount(policy)}</span>
              </td>
              <td className="col-count">
                <span className="policy-count-badge policy-count-badge--signatories">{getSignatoryCount(policy)}</span>
              </td>
              <td>
                <span className="policy-date">{formatDateTime(policy.updatedAt)}</span>
              </td>
              <td>
                <span
                  className="policy-ownership"
                  title={policy.loaInPlace && policy.loaInPlaceSince ? `In place since ${formatDateTime(policy.loaInPlaceSince)}` : undefined}
                >
                  {policy.loaInPlace ? 'Yes' : 'No'}
                </span>
              </td>
              <td>
                {policy.coaStatus ? (
                  renderCoaAgencyStatus(policy.coaStatus)
                ) : (
                  <span className="policy-ownership">{policy.inAgency ? 'Yes' : 'No'}</span>
                )}
              </td>
              {!hideActionColumn && (
                <td className="col-action" onClick={(e) => e.stopPropagation()}>
                  {renderAction(policy, workflowStatus)}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
